import { Controller } from '@hotwired/stimulus'
import DynamicSelect from '../../modules/dynamic_select'

export default class extends Controller {
  static targets = ['attribute', 'predicate', 'valueInput', 'dateValueInput', 'valueSelect', 'companyValueSelect', 'leadAuditorsSelect', 'technicalAuditorsSelect']

  static values = {
    predicates: Array,
    values: Array,
    valueType: String
  }

  declare readonly attributeTarget: HTMLSelectElement
  declare readonly predicateTarget: HTMLSelectElement
  declare readonly valueInputTarget: HTMLInputElement
  declare readonly dateValueInputTarget: HTMLInputElement
  declare readonly valueSelectTarget: HTMLSelectElement
  declare readonly companyValueSelectTarget: HTMLSelectElement
  declare readonly leadAuditorsSelectTarget: HTMLSelectElement
  declare readonly technicalAuditorsSelectTarget: HTMLSelectElement

  declare readonly hasFieldValueTypeTarget: boolean
  declare readonly hasCompanyValueSelectTarget: boolean
  declare readonly hasLeadAuditorsSelectTarget: boolean
  declare readonly hasTechnicalAuditorsSelectTarget: boolean

  declare predicatesValue: any[]
  declare valuesValue: any[]
  declare valueTypeValue: string

  predicatesSelect: DynamicSelect
  valuesSelect: DynamicSelect

  initialize (): void {
    this.predicatesSelect = new DynamicSelect(this.predicateTarget)
    this.valuesSelect = new DynamicSelect(this.valueSelectTarget)

    setTimeout(() => {
      this.attributeTarget.dispatchEvent(new Event('change'))
    })
  }

  update ({ detail: { predicates, values, valueType } }: { detail: { predicates: any[], values: any[], valueType: string } }): void {
    this.predicatesValue = predicates
    this.valuesValue = values
    this.valueTypeValue = valueType
    this.toggleValueTarget()
  }

  predicatesValueChanged (): void {
    this.predicatesSelect.reset(this.predicatesValue)
    this.toggleValueTarget()
  }

  valuesValueChanged (): void {
    this.valuesSelect.reset(this.valuesValue)
  }

  valueTypeValueChanged (): void {
    switch (this.valueTypeValue) {
      case 'float':
        this.valueInputTarget.type = 'number'
        break
      case 'datetime':
      case 'date':
        if (this.dateValueInputTarget.value.length <= 1) {
          this.dateValueInputTarget.value = new Date().toISOString().slice(0, 10)
        }

        if (!['yesterday', 'today', 'tomorrow'].includes(this.dateValueInputTarget.value)) {
          this.dateValueInputTarget.type = 'date'
        }
        break
      default:
        this.valueInputTarget.type = 'text'
    }
  }

  toggleValueTarget (): void {
    setTimeout(() => {
      const noValuePredicate = [
        'true', 'false', 'not_true', 'present', 'blank', 'null', 'not_null',
        'has_single_facility', 'has_multiple_facilities', 'has', 'does_not_have'
      ].includes(this.predicateTarget.value)
      const showSelect = this.valueTypeValue.includes('option') || this.valuesValue.length > 0
      const showInput = !showSelect
      const showDateInput = this.valueSelectTarget.value === 'custom_date'

      // Clear value when switching fields
      if (!showInput) this.valueInputTarget.value = ''
      if (!showDateInput) {
        this.dateValueInputTarget.value = ''
        this.dateValueInputTarget.type = 'date'
      }
      if (!showSelect) this.valueSelectTarget.value = ''

      this.toggle(this.valueInputTarget, !noValuePredicate && showInput)
      this.toggle(this.dateValueInputTarget, !noValuePredicate && showDateInput)
      this.toggleValueSelect(noValuePredicate, showSelect)
    })
  }

  private toggleValueSelect (noValuePredicate: boolean, showSelect: boolean): void {
    const isOrganizationId = this.attributeTarget.value === 'organization_id'
    const isAuditorUsers = this.attributeTarget.value === 'lead_auditor_users.id'
    const isTechnicalAuditorUsers = this.attributeTarget.value === 'technical_auditor_users.id'
    const show = showSelect && !noValuePredicate

    // Toggle the main value select dropdown
    this.toggle(this.valueSelectTarget, !isOrganizationId && !isAuditorUsers && show && !isTechnicalAuditorUsers)

    // Handle organization-specific logic
    this.toggleCompaniesSelectElements(isOrganizationId && show)

    // Handle auditor users-specific logic
    this.toggleAuditorUsersSelectElements(isAuditorUsers && show)
    this.toggleTechnicalAuditorsSelectElements(isTechnicalAuditorUsers && show)
  }

  private toggleCompaniesSelectElements (show: boolean): void {
    if (this.hasCompanyValueSelectTarget) {
      this.toggle(this.companyValueSelectTarget, show)
    }

    this.element.querySelectorAll('.ts-wrapper.ts-companies-select').forEach((element) => {
      this.toggle(element as HTMLSelectElement | HTMLInputElement, show)
    })
  }

  private toggleAuditorUsersSelectElements (show: boolean): void {
    if (this.hasLeadAuditorsSelectTarget) {
      this.toggle(this.leadAuditorsSelectTarget, show)
    }

    this.element.querySelectorAll('.ts-wrapper.ts-auditor-users-select').forEach((element) => {
      this.toggle(element as HTMLSelectElement | HTMLInputElement, show)
    })
  }

  private toggleTechnicalAuditorsSelectElements (show: boolean): void {
    if (this.hasTechnicalAuditorsSelectTarget) {
      this.toggle(this.technicalAuditorsSelectTarget, show)
    }

    this.element.querySelectorAll('.ts-wrapper.ts-technical-users-select').forEach((element) => {
      this.toggle(element as HTMLSelectElement | HTMLInputElement, show)
    })
  }

  toggle (target: HTMLInputElement | HTMLSelectElement, show: boolean): void {
    target.disabled = !show || target.getAttribute('data-readonly') === 'true'
    target.classList.toggle('hidden', !show)
  }
}
